import React, { useContext, useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import { InfoField } from '@components-clean/core/info-field';
import styles from '@components-clean/core/page-toolbar/styles.module.scss';
import Tooltip from '@components-clean/core/tooltip';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { ActionsGridParent, RiskMonitoringTab } from '@pages-clean/risk-assessment/constants';
import { RiskMonitoringObservationChipsValues } from '@pages-clean/risk-assessment/risk-monitoring/constants';
import { RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import SharedActionsGrid from '@pages-clean/risk-assessment/shared/actions/actions.grid';
import RiskArea from '@pages-clean/risk-assessment/shared/risk/risk';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';

const ObservationChildAreas = () => {
    const [chip, setChip] = useState<RiskMonitoringObservationChipsValues>(
        RiskMonitoringObservationChipsValues.Actions,
    );
    const { t }: { t: (key: string) => string } = useTranslation('ram', {
        keyPrefix: 'riskMonitoring',
    });
    const navigate = useNavigate();
    const { current, setCurrent } = useContext(RiskMonitoringContext);
    const {
        study,
        showDeleted: curShowDeleted,
        selectedObservation,
        selectedAction,
        selectedObservationRisk,
    } = current;
    const showDeleted = useBoolean(curShowDeleted);

    useEffect(() => {
        curShowDeleted ? showDeleted.setTrue() : showDeleted.setFalse();
    }, [curShowDeleted]);

    useEffect(() => {
        if (current.showDeleted !== showDeleted.value)
            setCurrent((prevState) => ({
                ...prevState,
                showDeleted: showDeleted.value,
            }));
    }, [showDeleted.value]);

    if (!selectedObservation)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('manageObservations.info.noParentRecordSelected')} layout="center" />
            </div>
        );

    return (
        <Card
            data-testid="risk-monitoring-child-area"
            style={{ height: '100%' }}
            title={
                <ChipList
                    data-testid="risk-monitoring-manage-obs-chip-list"
                    data={[
                        {
                            text: t('manageObservations.chips.actions'),
                            value: RiskMonitoringObservationChipsValues.Actions,
                        },
                        {
                            text: t('manageObservations.chips.risk'),
                            value: RiskMonitoringObservationChipsValues.Risk,
                        },
                    ]}
                    value={chip}
                    onChange={(event) => {
                        const selectedChip = event.value as RiskMonitoringObservationChipsValues;
                        if (selectedChip) setChip(selectedChip);
                    }}
                />
            }
            headerActions={
                chip === RiskMonitoringObservationChipsValues.Risk && selectedObservation ? (
                    <Tooltip parentTitle>
                        <button
                            aria-label={t('manageObservations.info.goToRisk')}
                            data-test-id="go-to-risk-button"
                            type="button"
                            title={t('manageObservations.info.goToRisk')}
                            className={styles.popOutPage}
                            onClick={() => {
                                NavigationService.setViewRiskDetails();
                                // Cannot use AllRoutes dictionary due to circular dependency.
                                navigate({
                                    pathname: `/risk-assessment/risk-log/id/${selectedObservationRisk?.recordId}`,
                                });
                            }}
                        >
                            <FontAwesomeIcon icon={['far', 'arrow-up-right-from-square']} />
                        </button>
                    </Tooltip>
                ) : undefined
            }
        >
            <div style={{ height: '100%' }} className="remove-card-padding">
                {chip === RiskMonitoringObservationChipsValues.Actions && (
                    <SharedActionsGrid
                        selectedAction={selectedAction}
                        showDeleted={showDeleted}
                        studyId={study?.id}
                        studyNumber={study?.studyNumber}
                        observations={[selectedObservation!]}
                        risks={[selectedObservationRisk!] as unknown[] as CompleteRiskFields[]}
                        parent={ActionsGridParent.RiskMonitoring}
                    />
                )}
                {chip === RiskMonitoringObservationChipsValues.Risk && (
                    <RiskArea
                        selectedObservation={selectedObservation}
                        observationIdCount={1}
                        parent={RiskMonitoringTab.ManageObservations}
                    />
                )}
            </div>
        </Card>
    );
};

export default ObservationChildAreas;
